import axios from 'axios';

const apiBaseURL = import.meta.env.VITE_API_URL ?? '/api';

// The operator's session is an httpOnly cookie the server sets; the page never sees the token.
export const api = axios.create({
  baseURL: apiBaseURL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const NO_REDIRECT = ['/auth/login', '/auth/profile', '/auth/logout'];

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const url = String(error.config?.url ?? '');
    if (
      error.response?.status === 401 &&
      !NO_REDIRECT.some((path) => url.endsWith(path)) &&
      window.location.pathname !== '/login'
    ) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

export interface AttendanceMonth {
  month: string;
  live: number;
  recording: number;
  totalMinutes: number;
}

export interface AttendanceSummaryParams {
  userId?: string;
  since?: string;
  until?: string;
  source?: string;
}

export async function fetchAttendanceByMonth(params: AttendanceSummaryParams = {}): Promise<AttendanceMonth[]> {
  const res = await api.get('/analytics/attendance/summary', {
    params: { ...params, groupBy: 'month' },
  });
  return res.data.data.items;
}

export interface MentorshipSeason {
  seasonId: number;
  seasonName: string;
  seasonIsActive: boolean;
  mentors: number;
  mentees: number;
  menteesPaired: number;
  menteesMatched: number;
  sessionsScheduled: number;
  sessionsAttended: number;
  members: number;
  pairingRate: number | null;
  attendanceRate: number | null;
  matchQuality: {
    pairedWithSuggestedMentor: number;
    pairedWithSuggestedMentorRate: number | null;
    affinityMatchedPairs: number;
    affinityMatchedRate: number | null;
  } | null;
}

export interface MentorshipTotals {
  mentors: number;
  mentees: number;
  sessionsScheduled: number;
  sessionsAttended: number;
  attendanceRate: number | null;
}

export interface MentorshipSummary {
  seasons: MentorshipSeason[];
  totals: MentorshipTotals;
}

export async function fetchMentorshipSummary(seasonId?: number): Promise<MentorshipSummary> {
  const res = await api.get('/analytics/mentorship/summary', {
    params: seasonId === undefined ? {} : { seasonId },
  });
  return res.data.data;
}

export type ReachColumn = 'members' | 'mentees' | 'paired' | 'attended' | 'mentors';

export interface ReachCategory extends Record<ReachColumn, number> {
  value: string;
  participationRate: number | null;
  pairingRate: number | null;
  attendanceRate: number | null;
}

export interface MentorshipReach {
  dimension: string;
  label: string;
  seasonId: number | null;
  categories: ReachCategory[];
  notAnswered: Record<ReachColumn, number>;
  totals: Record<ReachColumn, number> & {
    participationRate: number | null;
    pairingRate: number | null;
    attendanceRate: number | null;
  };
  dimensions: { key: string; label: string }[];
}

export async function fetchMentorshipReach(dimension: string, seasonId?: number): Promise<MentorshipReach> {
  const res = await api.get('/analytics/mentorship/reach', {
    params: seasonId === undefined ? { dimension } : { dimension, seasonId },
  });
  return res.data.data;
}
